import React, { useState } from 'react';
import { Team } from '../types/index.ts';
import { topicApi } from '../services/api.ts';

interface EditTeamModalProps {
  team: Team;
  onClose: () => void;
  onTeamUpdated: (updatedTeam: Team) => void;
  onTeamDeleted: () => void;
}

const EditTeamModal: React.FC<EditTeamModalProps> = ({ team, onClose, onTeamUpdated, onTeamDeleted }) => {
  const [teamName, setTeamName] = useState(team.teamName);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!teamName.trim()) {
      setError('Series name is required');
      return;
    }

    if (teamName.trim() === team.teamName) {
      onClose(); // No changes made
      return;
    }

    setIsUpdating(true);
    setError('');

    try {
      const response = await topicApi.updateTeam(team.teamId, teamName.trim());
      if (response.success && response.data) {
        onTeamUpdated(response.data);
        onClose();
      } else {
        setError(response.error || 'Failed to update series name');
        setIsUpdating(false);
      }
    } catch (error: any) {
      setError('Failed to update series name');
      setIsUpdating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    setDeleteError('');

    try {
      const response = await topicApi.deleteTeam(team.teamId);
      if (response.success) {
        onTeamDeleted();
      } else {
        setDeleteError(response.error || 'Failed to delete series');
        setIsDeleting(false);
      }
    } catch (error: any) {
      setDeleteError('Failed to delete series');
      setIsDeleting(false);
    }
  };

  const isDeleteConfirmValid = deleteConfirmText === team.teamName;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: '1px solid #ccc',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '4px 8px',
            lineHeight: 1
          }}
        >
          ✕
        </button>
        <h3>Edit Series</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Series Name:</label>
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter series name"
              required
              autoFocus
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={isUpdating} style={{ marginTop: '12px', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', border: '1px solid #ddd', backgroundColor: '#007bff', color: 'white' }}>
            {isUpdating ? 'Updating...' : 'Update Series Name'}
          </button>
        </form>

        {/* Delete Series Section */}
        <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
          {!showDeleteConfirm ? (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                backgroundColor: 'transparent',
                color: '#d32f2f',
                border: '1px solid #d32f2f',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Delete Series
            </button>
          ) : (
            <div>
              <p style={{ color: '#d32f2f', fontWeight: 500, marginBottom: '8px' }}>
                Are you sure you want to delete this series? This will delete the series and all the scheduled and past topics assigned to it. Type the series name to confirm deletion.
              </p>
              <div className="form-group">
                <label>Type "<strong>{team.teamName}</strong>" to confirm:</label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={team.teamName}
                  autoFocus
                />
              </div>

              {deleteError && <div className="error-message">{deleteError}</div>}

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={!isDeleteConfirmValid || isDeleting}
                  style={{
                    backgroundColor: isDeleteConfirmValid ? '#d32f2f' : '#ccc',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: isDeleteConfirmValid ? 'pointer' : 'not-allowed'
                  }}
                >
                  {isDeleting ? 'Deleting...' : 'Permanently Delete Series'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                    setDeleteError('');
                  }}
                  style={{
                    marginLeft: '8px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditTeamModal;
